import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, Plus, Printer, Save, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { money } from '../purchase-orders/poLine';
import { printStocktake } from './printStocktake';
import { loadCompanyProfile } from '../../../lib/companyProfile';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { Smartphone } from 'lucide-react';

export default function StocktakeTab({ lists, reload }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [nf, setNf] = useState({ name: '', category: '', location: '' });

  const load = () => api.get('/general-inventory/stocktakes').then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { const r = await api.post('/general-inventory/stocktakes', nf); setOpen(r.data); setNf({ name: '', category: '', location: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start stocktake'); }
  };
  const openOne = async (s) => { const r = await api.get(`/general-inventory/stocktakes/${s.id}`); setOpen(r.data); };
  const del = async (s) => { if (!window.confirm(`Delete ${s.number}?`)) { return; } await api.delete(`/general-inventory/stocktakes/${s.id}`); load(); };

  if (open) { return <Sheet st={open} onBack={() => { setOpen(null); load(); reload(); }} onChange={setOpen} />; }

  return (
    <div className="space-y-4" data-testid="gi-stocktake-tab">
      <Card><CardContent className="pt-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Name</Label><Input value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="e.g. End of month count" data-testid="st-name" /></div>
        <div><Label className="text-xs">Category (optional)</Label>
          <select className="border rounded-md h-10 px-2 w-full text-sm" value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} data-testid="st-category"><option value="">All</option>{lists.categories.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div><Label className="text-xs">Location (optional)</Label>
          <select className="border rounded-md h-10 px-2 w-full text-sm" value={nf.location} onChange={(e) => setNf({ ...nf, location: e.target.value })} data-testid="st-location"><option value="">All</option>{lists.locations.map((c) => <option key={c}>{c}</option>)}</select></div>
        <Button className="bg-slate-900" onClick={create} data-testid="st-start"><Plus className="h-4 w-4 mr-1" /> Start stocktake</Button>
      </CardContent></Card>
      <Card><CardContent className="pt-4">
        <table className="w-full text-sm"><thead className="bg-slate-100"><tr>{['No.', 'Name', 'Scope', 'Status', 'Variance', 'Started', ''].map((h) => <th key={h} className="text-left px-2 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">No stocktakes yet</td></tr>}
            {list.map((s) => (
              <tr key={s.id} className="border-t" data-testid={`st-row-${s.id}`}>
                <td className="px-2 py-2 font-medium">{s.number}</td><td className="px-2 py-2">{s.name}</td>
                <td className="px-2 py-2">{[s.category, s.location].filter(Boolean).join(' / ') || 'All items'}</td>
                <td className="px-2 py-2"><Badge variant="outline" className={s.status === 'Finalised' ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-700'}>{s.status}</Badge></td>
                <td className="px-2 py-2">{s.variance_value != null ? money(s.variance_value) : '—'}</td>
                <td className="px-2 py-2">{new Date(s.created_at).toLocaleDateString('en-AU')}</td>
                <td className="px-2 py-2 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openOne(s)} data-testid={`st-open-${s.id}`}>Open</Button>
                  {s.status === 'In Progress' && <Button size="sm" variant="outline" onClick={() => del(s)}><Trash2 className="h-3 w-3 text-red-600" /></Button>}
                </td>
              </tr>
            ))}
          </tbody></table>
      </CardContent></Card>
    </div>
  );
}

function Sheet({ st, onBack, onChange }) {
  const [blind, setBlind] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [opts, setOpts] = useState({ blind: true, packs: true, groupBy: 'location' });
  const hasPacks = st.lines.some((l) => l.units_per_pack);
  const [split, setSplit] = useState({});
  const [phoneOpen, setPhoneOpen] = useState(false);
  useEffect(() => { loadCompanyProfile(); }, []);
  const navigate = useNavigate();
  const phoneUrl = `${window.location.origin}/stocktake-count/${st.id}`;
  const done = st.status !== 'In Progress';
  const setCount = (id, v) => onChange({ ...st, lines: st.lines.map((l) => (l.item_id === id ? { ...l, counted: v === '' ? null : Number(v) } : l)) });
  const save = async () => { const r = await api.put(`/general-inventory/stocktakes/${st.id}`, { lines: st.lines.filter((l) => l.counted != null).map((l) => ({ item_id: l.item_id, counted: l.counted })) }); onChange(r.data); toast.success('Counts saved'); return r.data; };
  const finalise = async () => {
    if (!window.confirm('Finalise? Stock on hand will be set to the counted quantities.')) { return; }
    await save();
    const r = await api.post(`/general-inventory/stocktakes/${st.id}/finalise`);
    onChange(r.data); toast.success(`Finalised — ${r.data.items_adjusted} item(s) adjusted`);
  };
  const print = () => {
    if (!printStocktake(st, { ...opts, packs: opts.packs && hasPacks })) { toast.error('Pop-up blocked — allow pop-ups to print'); }
    setPrintOpen(false);
  };
  const setPacks = (l, k, v) => {
    const cur = { ...(split[l.item_id] || {}), [k]: v };
    setSplit({ ...split, [l.item_id]: cur });
    const empty = !cur.packs && !cur.loose;
    setCount(l.item_id, empty ? '' : String((Number(cur.packs) || 0) * Number(l.units_per_pack) + (Number(cur.loose) || 0)));
  };
  const counted = st.lines.filter((l) => l.counted != null).length;

  return (
    <div className="space-y-3" data-testid="st-sheet">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={onBack} data-testid="st-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <h2 className="text-xl font-bold">{st.number} — {st.name}</h2>
        <Badge variant="outline">{st.status}</Badge>
        <span className="text-sm text-slate-500">{counted}/{st.lines.length} counted</span>
        <div className="ml-auto flex gap-2 items-center">
          <label className="flex items-center gap-1 text-sm"><Checkbox checked={blind} onCheckedChange={(v) => setBlind(!!v)} data-testid="st-blind" /> Blind count</label>
          {!done && <Button variant="outline" onClick={() => setPhoneOpen(true)} data-testid="st-phone"><Smartphone className="h-4 w-4 mr-1" /> Count on phone</Button>}
          <Button variant="outline" onClick={() => setPrintOpen(true)} data-testid="st-print"><Printer className="h-4 w-4 mr-1" /> Print count sheet</Button>
          {!done && <Button variant="outline" onClick={save} data-testid="st-save"><Save className="h-4 w-4 mr-1" /> Save</Button>}
          {!done && <Button className="bg-slate-900" onClick={finalise} data-testid="st-finalise"><CheckCircle2 className="h-4 w-4 mr-1" /> Finalise</Button>}
        </div>
      </div>
      {done && <p className="text-sm text-green-700">Finalised — {st.items_adjusted} item(s) adjusted, variance {money(st.variance_value)}.</p>}
      <Card><CardContent className="pt-4 overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-slate-100"><tr>
          {['Location', 'Item', 'Unit', ...(blind ? [] : ['System']), 'Counted', ...(blind ? [] : ['Variance'])].map((h) => <th key={h} className="text-left px-2 py-2">{h}</th>)}
        </tr></thead>
          <tbody>{st.lines.map((l) => {
            const v = l.counted != null ? l.counted - l.expected : null;
            return (
              <tr key={l.item_id} className="border-t" data-testid={`st-line-${l.item_id}`}>
                <td className="px-2 py-1">{l.location || '—'}</td><td className="px-2 py-1 font-medium">{l.name}<div className="text-xs text-slate-500">{l.sku}</div></td><td className="px-2 py-1">{l.unit}</td>
                {!blind && <td className="px-2 py-1">{l.expected}</td>}
                <td className="px-2 py-1">
                  <Input className="h-8 w-24" type="number" step="any" disabled={done} value={l.counted ?? ''} onChange={(e) => setCount(l.item_id, e.target.value)} data-testid={`st-count-${l.item_id}`} />
                  {l.units_per_pack && !done ? (
                    <div className="flex items-center gap-1 mt-1 text-[11px] text-slate-500">
                      <Input className="h-7 w-14 text-xs" type="number" min="0" placeholder={l.pack_name || 'packs'} value={split[l.item_id]?.packs ?? ''} onChange={(e) => setPacks(l, 'packs', e.target.value)} data-testid={`st-packs-${l.item_id}`} />
                      ×{l.units_per_pack} +
                      <Input className="h-7 w-14 text-xs" type="number" min="0" placeholder="loose" value={split[l.item_id]?.loose ?? ''} onChange={(e) => setPacks(l, 'loose', e.target.value)} data-testid={`st-loose-${l.item_id}`} />
                    </div>
                  ) : null}
                </td>
                {!blind && <td className={`px-2 py-1 ${v > 0 ? 'text-green-700' : v < 0 ? 'text-red-600' : ''}`}>{v == null ? '—' : `${v > 0 ? '+' : ''}${v} (${money(v * (l.unit_cost || 0))})`}</td>}
              </tr>
            );
          })}</tbody></table>
      </CardContent></Card>
      <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
        <DialogContent className="max-w-sm" data-testid="st-phone-dialog">
          <DialogHeader><DialogTitle>Count on a phone</DialogTitle><DialogDescription>Scan with the phone camera, sign in with the staff member&apos;s QORDEL login, pick a location and count. Several people can count different areas at once; counts save straight away.</DialogDescription></DialogHeader>
          <div className="flex justify-center p-3 bg-white rounded-lg border"><QRCodeSVG value={phoneUrl} size={200} data-testid="st-phone-qr" /></div>
          <p className="text-xs text-slate-500 break-all text-center" data-testid="st-phone-url">{phoneUrl}</p>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { navigator.clipboard?.writeText(phoneUrl); toast.success('Link copied'); }}>Copy link</Button>
            <Button className="flex-1 bg-slate-900" onClick={() => navigate(`/stocktake-count/${st.id}`)} data-testid="st-phone-open">Open here</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-md" data-testid="st-print-dialog">
          <DialogHeader><DialogTitle>Print count sheet</DialogTitle><DialogDescription>A4 form for staff to count on, with sign-off. Use your printer&apos;s &quot;Save as PDF&quot; to keep a copy.</DialogDescription></DialogHeader>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={opts.blind} onCheckedChange={(v) => setOpts({ ...opts, blind: !!v })} data-testid="st-print-blind" /> Blind count (hide system quantities)</label>
          {hasPacks && <label className="flex items-center gap-2 text-sm"><Checkbox checked={opts.packs} onCheckedChange={(v) => setOpts({ ...opts, packs: !!v })} data-testid="st-print-packs" /> Full packs + loose columns</label>}
          <label className="text-sm">Group by
            <select className="border rounded-md h-9 px-2 ml-2 text-sm" value={opts.groupBy} onChange={(e) => setOpts({ ...opts, groupBy: e.target.value })} data-testid="st-print-group">
              <option value="location">Location (walk order)</option><option value="category">Category</option>
            </select>
          </label>
          <Button className="bg-slate-900" onClick={print} data-testid="st-print-go"><Printer className="h-4 w-4 mr-1" /> Print</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
